<script setup lang="ts">
import { mdiLinkVariant } from '@mdi/js';
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import DeviceStatusIslandDetectorInterfaceRow from './DeviceStatusIslandDetectorInterfaceRow.vue';
import DeviceStatusIslandUnicheckRow from './DeviceStatusIslandUnicheckRow.vue';
import {
  STATE_INFO,
  batteryDisplayOf,
  lowestBatteryOf,
  worstStateOf,
} from './deviceStatusIslandState';
import { DeviceStatusIslandItem } from './deviceStatusIslandTypes';

const props = defineProps<{
  items: DeviceStatusIslandItem[];
}>();

const islandRoot = ref<HTMLElement | null>(null);

const displayItems = ref<DeviceStatusIslandItem[]>([...props.items]);

const visible = computed(() => displayItems.value.length > 0);

const expanded = ref(false);

const singleItem = computed(() =>
  displayItems.value.length === 1 ? displayItems.value[0] : undefined,
);
const worstState = computed(() => worstStateOf(displayItems.value));
const stateInfo = computed(() => STATE_INFO[worstState.value ?? 'online']);
const batteryInfo = computed(() => {
  const battery = lowestBatteryOf(displayItems.value);
  return battery ? batteryDisplayOf(battery) : undefined;
});

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function morph(mutate: () => void, options?: { noOvershoot?: boolean }) {
  if (!document.startViewTransition || prefersReducedMotion()) {
    mutate();
    return;
  }

  const root = document.documentElement;
  if (options?.noOvershoot) {
    root.classList.add('island-morph-no-overshoot');
  }
  const transition = document.startViewTransition(async () => {
    mutate();
    await nextTick();
  });
  if (options?.noOvershoot) {
    transition.finished.finally(() => root.classList.remove('island-morph-no-overshoot'));
  }
}

function expand() {
  if (visible.value && !expanded.value) {
    morph(() => {
      expanded.value = true;
    });
  }
}

function collapse() {
  if (expanded.value) {
    // Putting the island away should settle, not wobble.
    morph(
      () => {
        expanded.value = false;
      },
      { noOvershoot: true },
    );
  }
}

function onWindowKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    collapse();
  }
}

function onWindowPointerdown(event: PointerEvent) {
  const el = islandRoot.value;
  if (el && !el.contains(event.target as Node)) {
    collapse();
  }
}

watch(expanded, (isOpen) => {
  if (isOpen) {
    window.addEventListener('keydown', onWindowKeydown);
    window.addEventListener('pointerdown', onWindowPointerdown);
  } else {
    window.removeEventListener('keydown', onWindowKeydown);
    window.removeEventListener('pointerdown', onWindowPointerdown);
  }
});

onUnmounted(() => {
  window.removeEventListener('keydown', onWindowKeydown);
  window.removeEventListener('pointerdown', onWindowPointerdown);
});

watch(visible, (isVisible) => {
  if (!isVisible) {
    expanded.value = false;
  }
});

watch(
  () => props.items,
  (next) => {
    if (displayItems.value.length === 0 || next.length === 0) {
      displayItems.value = [...next];
      return;
    }
    if (JSON.stringify(next) === JSON.stringify(displayItems.value)) {
      displayItems.value = [...next];
      return;
    }
    morph(() => {
      displayItems.value = [...next];
    });
  },
);

// The Host collapses the panel on route change; expose the same morphing
// collapse the internal Escape / outside-click use.
defineExpose({ collapse });
</script>

<template>
  <transition name="island-pop" appear>
    <div v-if="visible" ref="islandRoot" class="island" :class="{ 'island--expanded': expanded }">
      <button
        type="button"
        class="island__pill"
        :tabindex="expanded ? -1 : 0"
        :aria-hidden="expanded"
        :aria-label="$t('DeviceStatusIslandTitle')"
        :aria-expanded="expanded"
        @click="expand"
      >
        <v-icon :color="stateInfo.color" :icon="stateInfo.icon" size="14" />

        <span class="island__pill-label text-body-2">
          <template v-if="singleItem">{{ singleItem.headline }}</template>
          <template v-else>{{
            $t('DeviceStatusIslandActiveDevices', { count: displayItems.length })
          }}</template>
        </span>

        <transition name="island-link-pop">
          <v-icon
            v-if="singleItem?.state === 'connected'"
            class="island__pill-link"
            :icon="mdiLinkVariant"
            size="14"
            :aria-label="$t('DeviceStatusIslandConnected')"
          />
        </transition>

        <span v-if="singleItem && batteryInfo" class="island__pill-battery">
          <v-icon
            :color="batteryInfo.color"
            :icon="batteryInfo.icon"
            size="18"
            :class="{ 'island__pill-battery-icon--sideways': batteryInfo.sideways }"
          />
        </span>
      </button>

      <div
        class="island__panel"
        :aria-hidden="!expanded"
        :aria-label="$t('DeviceStatusIslandTitle')"
      >
        <template v-for="item in displayItems" :key="item.id">
          <DeviceStatusIslandUnicheckRow v-if="item.type === 'unicheck'" :item="item" />
          <DeviceStatusIslandDetectorInterfaceRow v-else :item="item" :expanded="expanded" />
        </template>
      </div>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
.island {
  position: fixed;

  --island-top: calc((64px - 36px) / 2);
  top: var(--island-top);
  left: 50%;
  transform: translateX(-50%);
  z-index: 1999;

  view-transition-name: device-status-island;

  max-width: 92vw;
  border-radius: 18px;
  background: rgb(var(--v-theme-secondary-darken-4));
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  overflow: hidden;

  &--expanded {
    border-radius: 16px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
  }
}

.island__pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  min-width: 0;
  max-width: min(60vw, 420px);

  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;

  .island--expanded & {
    display: none;
  }

  &-label {
    flex: 0 1 auto;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &-battery {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    flex: 0 0 auto;
  }

  &-battery-icon--sideways {
    transform: rotate(90deg);
  }

  &-link {
    flex: 0 0 auto;
    transform-origin: center;
  }
}

.island__panel {
  display: none;
  width: fit-content;
  min-width: 288px;
  max-width: min(560px, 92vw);
  padding: 10px 14px 12px;

  --v-theme-on-surface: 255, 255, 255;
  --v-theme-surface: 21, 21, 21;
  --v-border-color: 255, 255, 255;
  --v-border-opacity: 0.14;

  .island--expanded & {
    display: block;
  }
}

.island-pop-enter-active,
.island-pop-leave-active {
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.island-pop-enter-from,
.island-pop-leave-to {
  transform: translateX(-50%) translateY(calc(-100% - var(--island-top)));
}

@media (prefers-reduced-motion: reduce) {
  .island-pop-enter-active,
  .island-pop-leave-active {
    animation: none;
    transition: none;
  }
}

// connection link animation
.island-link-pop-enter-active {
  animation: island-link-pop-in 0.6s 0.3s backwards ease-out;
}
.island-link-pop-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}
.island-link-pop-leave-to {
  opacity: 0;
  transform: scale(0.5);
}

@keyframes island-link-pop-in {
  0% {
    opacity: 0;
    transform: scale(0);
  }
  12% {
    opacity: 1;
    transform: scale(1.25); /* A4 pop */
  }
  18% {
    transform: scale(1);
  }
  30% {
    transform: scale(1.45); /* C5 pop */
  }
  37% {
    transform: scale(1.12);
  }
  50% {
    transform: scale(2); /* E5 pop (climax on the highest note) */
  }
  70% {
    transform: scale(0.92);
  }
  100% {
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .island-link-pop-enter-active,
  .island-link-pop-leave-active {
    animation: none;
    transition: none;
  }
}
</style>

<style>
::view-transition-group(device-status-island) {
  animation-duration: 0.25s;
  animation-timing-function: cubic-bezier(0.34, 1.56, 0.64, 1);
}

:root.island-morph-no-overshoot::view-transition-group(device-status-island) {
  animation-timing-function: cubic-bezier(0.2, 0, 0, 1);
}

::view-transition-old(device-status-island),
::view-transition-new(device-status-island) {
  height: 100%;
}

@media (prefers-reduced-motion: reduce) {
  ::view-transition-group(device-status-island),
  ::view-transition-old(device-status-island),
  ::view-transition-new(device-status-island) {
    animation: none;
  }
}
</style>
